import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { Dispatch } from 'redux';
import { DocumentType, Media } from '@synergy/models';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';
import { removeUploadedFile, uploadFileStart, uploadFiles } from '@synergy/data-access';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { Offer } from '../models/offer';
import { ShopPrice } from '../api/offer-api';
import { SupplierWithContact } from '../../features/inquiries/models/supplier-with-contact';

export const ADD_OR_UPDATE_OFFER = 'ADD_OR_UPDATE_OFFER';
export const UPDATE_OFFER = 'UPDATE_OFFER';
export const RECOMMEND_OFFER = 'RECOMMEND_OFFER';
export const SEND_MATRIX = 'SEND_MATRIX';

const updateOffer = (offer: Offer) => {
    return {
        type: UPDATE_OFFER,
        offer,
    };
};

const addOrUpdateOffer = (accountPurchaseId: number, offer: Offer) => {
    return {
        type: ADD_OR_UPDATE_OFFER,
        accountPurchaseId,
        offer,
    };
};

const recommendOffer = (newRecommendedOfferId: number, isRecommended: boolean) => {
    return {
        type: RECOMMEND_OFFER,
        newRecommendedOfferId,
        isRecommended,
    };
};

const sendMatrix = () => {
    return {
        type: SEND_MATRIX,
    };
};

export const sendInquiryOffers = (inquiryId: number, suppliers: SupplierWithContact[]) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.offer.createOffers(inquiryId, suppliers);

        if (response.success) {
            response.result.items.forEach(offer => {
                if (offer.accountPurchaseId) {
                    dispatch(addOrUpdateOffer(offer.accountPurchaseId, offer));
                }
            });
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const addOfferToAccountPurchase = (accountPurchaseId: number, offer: Partial<Offer>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const { paymentTerm, ...requestBase } = offer;
        const offerRequest = { ...requestBase, paymentTermId: paymentTerm?.id };

        const response = await api.offer.addOffer(accountPurchaseId, offerRequest);

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.offerSavedSuccess')));
            dispatch(addOrUpdateOffer(accountPurchaseId, response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const declineOffer = (id: number, subject: string, message: string) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        const response = await api.offer.declineOffer(id, subject, message);

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.offerDeclined')));
            dispatch(updateOffer(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const updateOfferOfInquiry = (offer: Partial<Offer>, newFiles: File[], mediaToRemove: Media[]) => {
    return async (dispatch: Dispatch, getState: () => AppState, { api }: { api: Api }) => {
        // Update media
        const promises: Promise<void>[] = [];
        promises.push(uploadFiles(DocumentType.Offer, offer.id || -1, newFiles)(dispatch, getState));
        mediaToRemove.forEach(media =>
            promises.push(removeUploadedFile(DocumentType.Offer, offer.id || -1, media)(dispatch, getState))
        );
        await Promise.all(promises);

        // Update offer
        const { paymentTerm, ...requestBase } = offer;
        const offerRequest = { ...requestBase, paymentTermId: paymentTerm?.id };
        const response = await api.offer.updateOffer(offerRequest);

        if (response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.offerSavedSuccess')));
            dispatch(updateOffer(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.offerSavedError')));
        }
    };
};

export const updateOfferRecommendation = (newRecommendedOfferId: number, isRecommended: boolean) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(recommendOffer(newRecommendedOfferId, isRecommended));
        const response = await api.offer.recommendOffer(newRecommendedOfferId, isRecommended);

        if (!response.success) {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchShopPrices = (accountPurchaseId: number, supplierAccountId: number) => {
    return async (_dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }): Promise<ShopPrice[]> => {
        const response = await api.offer.getShopPrices(accountPurchaseId, supplierAccountId);

        return response.success ? response.result : [];
    };
};

export const sendPriceComparison = (
    inquiryId: number,
    accountPurchaseId: number,
    supplierAccountIds: number[],
    allDeliveryAddresses = false,
    supplierAccountIdsByAddress?: Record<number, number[]>
) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(sendMatrix());
        const response = await api.offer.sendPriceComparison(
            accountPurchaseId,
            supplierAccountIds,
            allDeliveryAddresses,
            supplierAccountIdsByAddress
        );

        if (response.success) {
            // The answer carries no date; the file was made just now, and the
            // card says when the price info went out (#2256).
            response.result.createdAt = response.result.createdAt ?? new Date();
            dispatch(uploadFileStart(DocumentType.Inquiry, inquiryId, response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
